import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CalendarBlank, ArrowRight, Tag, MagnifyingGlass } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const categories = [
    "all",
    "For Families Planning a Sale",
    "Upcoming Estate Sales",
    "Downsizing Tips",
    "Estate Cleanout and Liquidation"
  ];

  const filteredPosts = posts.filter(post => {
    const matchesCategory = activeCategory === "all" || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = posts[0];

  return (
    <div data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
              Our Blog
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[#1C1914] leading-tight mb-6">
              Estate Sale Insights & Updates
            </h1>
            <p className="text-lg text-[#5C564D] leading-relaxed max-w-2xl mx-auto">
              Helpful guides for families planning estate sales, tips for shoppers, and updates on upcoming sales throughout Fargo-Moorhead.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto mb-12">
            <div className="relative">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C564D]" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-[#E2DCD0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent bg-white text-[#1C1914]"
                data-testid="blog-search"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? "bg-[#2C4C3B] text-white"
                    : "bg-white border border-[#E2DCD0] text-[#5C564D] hover:bg-[#E2DCD0]"
                }`}
                data-testid={`blog-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category === "all" ? "All Posts" : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {!loading && featuredPost && activeCategory === "all" && !searchQuery && (
        <section className="py-16 px-6 md:px-12 lg:px-24 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-serif font-medium text-[#1C1914] mb-8">Featured Article</h2>
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="grid md:grid-cols-2 gap-8 bg-[#F7F5F0] rounded-lg overflow-hidden group"
              data-testid="featured-post"
            >
              <div className="aspect-video md:aspect-auto overflow-hidden">
                {featuredPost.image ? (
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.image_alt || featuredPost.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-[#2C4C3B]/10 flex items-center justify-center">
                    <span className="text-6xl font-serif text-[#2C4C3B]/30">LL</span>
                  </div>
                )}
              </div>
              <div className="p-8 flex flex-col justify-center">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#C5A059] mb-3">
                  {featuredPost.category}
                </span>
                <h3 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-4 group-hover:text-[#2C4C3B] transition-colors">
                  {featuredPost.title}
                </h3>
                <p className="text-[#5C564D] mb-4 line-clamp-3">{featuredPost.excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-[#5C564D]">
                  <span className="flex items-center gap-2">
                    <CalendarBlank className="w-4 h-4" />
                    {new Date(featuredPost.published_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#5C564D]">Loading articles...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5C564D]">No articles found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="bg-white border border-[#E2DCD0] rounded-lg overflow-hidden group card-hover"
                  data-testid={`blog-post-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    {post.image ? (
                      <img 
                        src={post.image} 
                        alt={post.image_alt || post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#2C4C3B]/5 flex items-center justify-center">
                        <span className="text-4xl font-serif text-[#2C4C3B]/20">LL</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag weight="duotone" className="w-4 h-4 text-[#C5A059]" />
                      <span className="text-xs font-medium uppercase tracking-wider text-[#C5A059]">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-3 group-hover:text-[#2C4C3B] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-[#5C564D] text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#5C564D] flex items-center gap-1">
                        <CalendarBlank className="w-3 h-3" />
                        {new Date(post.published_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <span className="text-[#2C4C3B] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#2C4C3B]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-medium text-white mb-6">
            Need Help With an Estate Sale?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Contact Legacy Liquidators LLC for a free consultation. We serve families throughout Fargo-Moorhead and surrounding communities.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#A88746] text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
            data-testid="blog-cta-contact"
          >
            Request a Consultation
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
